/**
 * "Gestionar negocio" (Super Admin)
 *
 * El super admin abre el panel de un negocio dentro de un modal: un iframe de
 * esta misma aplicación que se muestra exactamente como lo ve el propietario.
 * El iframe NO inicia sesión por su cuenta (no usa la cookie de renovación ni
 * el almacenamiento de respaldo, que comparte con la ventana principal): pide
 * el token a la ventana principal por postMessage y envía en cada petición la
 * cabecera X-Business-Context con el negocio elegido.
 *
 * El negocio se guarda en `window.name` del iframe (sobrevive a recargas y a
 * la navegación interna, a diferencia de un parámetro de la URL).
 */
export const BUSINESS_CONTEXT_HEADER = 'X-Business-Context';
export const BUSINESS_CONTEXT_CHANNEL = 'tusala-business-context';
const WINDOW_NAME_PREFIX = 'tusala-business-context:';

export type BusinessContextChildMessage =
  | { channel: typeof BUSINESS_CONTEXT_CHANNEL; type: 'token-request'; requestId: number; refresh: boolean }
  | { channel: typeof BUSINESS_CONTEXT_CHANNEL; type: 'context-loaded'; business: unknown }
  | { channel: typeof BUSINESS_CONTEXT_CHANNEL; type: 'context-failed'; message: string }
  | { channel: typeof BUSINESS_CONTEXT_CHANNEL; type: 'session-ended' }
  | { channel: typeof BUSINESS_CONTEXT_CHANNEL; type: 'close' }
  | { channel: typeof BUSINESS_CONTEXT_CHANNEL; type: 'manage-owner-account' };

export type BusinessContextParentMessage =
  | { channel: typeof BUSINESS_CONTEXT_CHANNEL; type: 'token'; requestId: number; token: string | null };

export const businessContextWindowName = (businessId: number): string => `${WINDOW_NAME_PREFIX}${businessId}`;

export const BUSINESS_CONTEXT_QUERY_PARAM = 'gestion';

const parseBusinessId = (value: unknown): number | null => {
  const id = Number(value);
  return Number.isInteger(id) && id > 0 ? id : null;
};

const readEmbeddedBusinessContextId = (): number | null => {
  if (typeof window === 'undefined') return null;
  try {
    if (window.parent === window) return null;
    // Solo dentro del modal de esta misma aplicación: si la ventana
    // contenedora es de otro origen, leer su location lanza una excepción.
    if (window.parent.location.origin !== window.location.origin) return null;
    const name = String(window.name || '');
    let id = name.startsWith(WINDOW_NAME_PREFIX) ? parseBusinessId(name.slice(WINDOW_NAME_PREFIX.length)) : null;
    if (!id) {
      // Respaldo: el modal también lo indica en la URL de carga. Se fija en
      // window.name para que una recarga del iframe conserve el negocio.
      id = parseBusinessId(new URLSearchParams(window.location.search).get(BUSINESS_CONTEXT_QUERY_PARAM));
      if (id) window.name = `${WINDOW_NAME_PREFIX}${id}`;
    }
    return id;
  } catch {
    return null;
  }
};

/** Negocio que esta ventana gestiona (solo dentro del modal), o null. Se lee una sola vez. */
export const EMBEDDED_BUSINESS_CONTEXT_ID: number | null = readEmbeddedBusinessContextId();

export const isBusinessContextMessage = (data: unknown): data is { channel: string; type: string } =>
  !!data && typeof data === 'object' && (data as { channel?: unknown }).channel === BUSINESS_CONTEXT_CHANNEL;

/** Mensaje del iframe hacia la ventana del super admin (mismo origen únicamente). */
type WithoutChannel<T> = T extends unknown ? Omit<T, 'channel'> : never;

export const postToBusinessContextHost = (message: WithoutChannel<BusinessContextChildMessage>): void => {
  if (!EMBEDDED_BUSINESS_CONTEXT_ID) return;
  try {
    window.parent.postMessage({ channel: BUSINESS_CONTEXT_CHANNEL, ...message }, window.location.origin);
  } catch { /* la ventana principal ya no existe */ }
};
